<template>
  <div class="admin-page">
    <div class="admin-header">
      <h1>{{ isEdit ? 'Edit Cohort' : 'New Cohort' }}</h1>
      <router-link to="/pointe/admin/cohorts" class="back-link">← Back to Cohorts</router-link>
    </div>

    <div v-if="loading" class="loading-wrap">
      <v-progress-circular indeterminate color="#1a1a1a"></v-progress-circular>
    </div>

    <form v-else @submit.prevent="save" class="cohort-form">
      <div class="form-group">
        <label>Cohort Name *</label>
        <input v-model="form.name" type="text" required class="form-input" placeholder="e.g. Spring 2026" />
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Open Date *</label>
          <input v-model="form.openDate" type="date" required class="form-input" />
        </div>
        <div class="form-group">
          <label>Close Date *</label>
          <input v-model="form.closeDate" type="date" required class="form-input" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Application Fee ($) *</label>
          <input v-model.number="form.applicationFee" type="number" min="0" step="0.01" required class="form-input" placeholder="97" />
        </div>
        <div class="form-group">
          <label>Cohort Fee ($) *</label>
          <input v-model.number="form.cohortFee" type="number" min="0" step="0.01" required class="form-input" placeholder="497" />
        </div>
      </div>

      <div class="form-group">
        <label>Status</label>
        <select v-model="form.status" class="form-input">
          <option value="upcoming">Upcoming</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      <div class="form-group">
        <label>Dropbox Video Request URL</label>
        <input v-model="form.dropboxVideoRequestUrl" type="url" class="form-input" placeholder="https://www.dropbox.com/request/..." />
        <p class="field-hint">Dancers use this link to upload their audition video. Set up a File Request in Dropbox, then paste the link here.</p>
      </div>

      <p v-if="error" class="error-text">{{ error }}</p>

      <div class="form-actions">
        <router-link to="/pointe/admin/cohorts" class="btn-secondary">Cancel</router-link>
        <button type="submit" class="btn-primary" :disabled="saving">
          {{ saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Cohort') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { db } from '@/firebase'

export default {
  props: {
    id: { type: String, default: null }
  },
  data () {
    return {
      loading: false,
      saving: false,
      error: '',
      form: {
        name: '',
        openDate: '',
        closeDate: '',
        applicationFee: null,
        cohortFee: null,
        status: 'upcoming',
        dropboxVideoRequestUrl: ''
      }
    }
  },
  computed: {
    isEdit () {
      return !!this.$route.params.id
    }
  },
  async created () {
    if (this.isEdit) {
      this.loading = true
      const doc = await db.collection('pointe-cohorts').doc(this.$route.params.id).get()
      if (doc.exists) {
        const d = doc.data()
        this.form = {
          name: d.name || '',
          openDate: this.tsToDateInput(d.openDate),
          closeDate: this.tsToDateInput(d.closeDate),
          applicationFee: d.applicationFee || null,
          cohortFee: d.cohortFee || null,
          status: d.status || 'upcoming',
          dropboxVideoRequestUrl: d.dropboxVideoRequestUrl || ''
        }
      }
      this.loading = false
    }
  },
  methods: {
    tsToDateInput (ts) {
      if (!ts) return ''
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toISOString().slice(0, 10)
    },
    async save () {
      this.saving = true
      this.error = ''
      try {
        const data = {
          name: this.form.name.trim(),
          openDate: new Date(this.form.openDate),
          closeDate: new Date(this.form.closeDate),
          applicationFee: Number(this.form.applicationFee),
          cohortFee: Number(this.form.cohortFee),
          status: this.form.status,
          dropboxVideoRequestUrl: this.form.dropboxVideoRequestUrl.trim()
        }
        if (this.isEdit) {
          await db.collection('pointe-cohorts').doc(this.$route.params.id).update(data)
        } else {
          await db.collection('pointe-cohorts').add({ ...data, createdAt: new Date() })
        }
        this.$router.push('/pointe/admin/cohorts')
      } catch (e) {
        this.error = e.message
      }
      this.saving = false
    }
  }
}
</script>

<style scoped>
.admin-page {
  max-width: 700px;
  margin: 0 auto;
  padding: 32px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
}

.admin-header h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 28px;
  color: #1a1a1a;
  margin: 0;
}

.back-link { font-size: 14px; color: #666; text-decoration: none; }
.back-link:hover { color: #1a1a1a; }

.loading-wrap { padding: 40px; text-align: center; }

.cohort-form { display: flex; flex-direction: column; gap: 0; }

.form-row { display: flex; gap: 16px; }
.form-row .form-group { flex: 1; }

.form-group { margin-bottom: 20px; }

.form-group label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #1a1a1a;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  border: 1px solid #e8e5e1;
  padding: 10px 12px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  border-radius: 2px;
  box-sizing: border-box;
  color: #1a1a1a;
  background: #fff;
}

.form-input:focus { outline: none; border-color: #1a1a1a; }

.field-hint { font-size: 13px; color: #888; margin: 6px 0 0; }

.form-actions { display: flex; gap: 12px; justify-content: flex-end; margin-top: 12px; }

.btn-primary {
  background: #1a1a1a;
  color: #fff;
  border: none;
  padding: 12px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  cursor: pointer;
  border-radius: 2px;
}

.btn-primary:hover { background: #333; }
.btn-primary:disabled { opacity: 0.5; cursor: default; }

.btn-secondary {
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 12px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  cursor: pointer;
  border-radius: 2px;
  text-decoration: none;
  display: inline-block;
  box-sizing: border-box;
}

.error-text { color: #c00; font-size: 14px; }
</style>
